import React, { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { loadGoogleMaps, COMPANY_HQ_ADDRESS } from "@/lib/googleMaps";
import { MapPin, Clock, Navigation, Loader2 } from "lucide-react";

export default function SiteAddressPicker({ value, onChange }) {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [address, setAddress] = useState(value?.address || "");

  useEffect(() => {
    let mounted = true;
    loadGoogleMaps().then(() => { if (mounted) setReady(true); }).catch(() => {});
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (!ready || !inputRef.current || autocompleteRef.current) return;
    const ac = new window.google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: "it" },
      fields: ["formatted_address", "address_components", "geometry"],
    });
    autocompleteRef.current = ac;
    ac.addListener("place_changed", () => {
      const place = ac.getPlace();
      if (!place.geometry) return;
      handleSelect(place);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  const handleSelect = (place) => {
    const comp = (type) => place.address_components?.find(c => c.types.includes(type));
    const comune = comp("locality")?.long_name || comp("administrative_area_level_3")?.long_name || "";
    const provincia = comp("administrative_area_level_2")?.short_name || "";
    const cap = comp("postal_code")?.long_name || "";
    const lat = place.geometry.location.lat();
    const lng = place.geometry.location.lng();
    const fullAddress = place.formatted_address || address;
    setAddress(fullAddress);

    const base = { address: fullAddress, comune, provincia, cap, lat, lng };
    onChange({ ...base, distance_text: null, duration_text: null, distance_value: null, duration_value: null });

    setCalculating(true);
    const svc = new window.google.maps.DirectionsService();
    svc.route({
      origin: COMPANY_HQ_ADDRESS,
      destination: { lat, lng },
      travelMode: window.google.maps.TravelMode.DRIVING,
    }, (result, status) => {
      setCalculating(false);
      if (status === "OK" && result.routes?.[0]?.legs?.[0]) {
        const leg = result.routes[0].legs[0];
        onChange({
          ...base,
          distance_text: leg.distance.text, distance_value: leg.distance.value,
          duration_text: leg.duration.text, duration_value: leg.duration.value,
        });
      }
    });
  };

  return (
    <div>
      <Label>Indirizzo cantiere *</Label>
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input ref={inputRef} className="pl-9" value={address}
               onChange={(e) => { setAddress(e.target.value); onChange({ address: e.target.value }); }}
               onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
               placeholder="Inizia a digitare via, comune..." required data-testid="site-address-autocomplete" />
      </div>
      {!ready && <p className="text-[10px] text-slate-400 mt-1">Caricamento mappe...</p>}
      {(value?.comune || value?.cap) && (
        <div className="flex flex-wrap gap-1 mt-2">
          {value.comune && <Badge variant="outline" className="text-[10px]">{value.comune}</Badge>}
          {value.provincia && <Badge variant="outline" className="text-[10px]">{value.provincia}</Badge>}
          {value.cap && <Badge variant="outline" className="text-[10px]">{value.cap}</Badge>}
        </div>
      )}
      {calculating && (
        <p className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
          <Loader2 className="w-3 h-3 animate-spin" /> Calcolo distanza dalla sede...
        </p>
      )}
      {value?.duration_text && (
        <div className="flex items-center gap-3 mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
          <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-orange-500" />{value.duration_text}</span>
          <span className="flex items-center gap-1"><Navigation className="w-3 h-3 text-orange-500" />{value.distance_text}</span>
          <span className="text-slate-400">dalla sede (Pedriano)</span>
        </div>
      )}
      <p className="text-[10px] text-slate-400 mt-1">
        Seleziona un suggerimento dall'elenco per compilare comune/provincia/CAP e calcolare la distanza.
      </p>
    </div>
  );
}
